import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import PickItem from './PickItem';
import LockItem from './LockItem';
import styles from '../styles/BetSlip.module.css';
import { useGameContext } from '../context/GameContext';
import { useAuth } from '../context/AuthContext';
import LeagueLockItem from './LeagueLockItem';
import ConfirmationDialog from './ConfirmationDialog';
import { getByeWeekRequirement } from '../adminApi';

const DragHandleIcon = () => (
  <svg className={styles.dragHandleIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="72px" height="72px">
    <path d="M20 9H4v2h16V9zM4 15h16v-2H4v2z"/>
  </svg>
);

const BetSlip = ({
  week,
  calendar,
  onClose,
  watchedPicks,
  setWatchedPicks,
  locks,
  toggleOpen,
  isBetSlipOpen,
  games
}) => {
  const { user } = useAuth();
  const {
    toggleWatchPick,
    clearWatchesForWeek,
    requestDirectLock,
    getMaxPicksForWeek,
    getLocksForWeek,
    activeUserCount,
    fetchActiveUserCount,
    verificationState,
    handleVerificationResponse,
    byeWeekState,
    toggleByeWeekOption,
    toggleByeWeekPending,
    currentWeek,
    userSeasonLocks,
    fetchUserSeasonLocks,
    isFutureWeekLockAllowed
  } = useGameContext();

  // Get current user's lock count for this week
  const userLocksCount = useMemo(() => {
    const locksForWeek = getLocksForWeek(week);
    if (!locksForWeek || !Array.isArray(locksForWeek)) return 0;
    return locksForWeek.filter(lock =>
      lock.userId === user?.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;
  }, [getLocksForWeek, week, user]);

  // Check if current user has reached max picks for this week
  const hasReachedMaxPicks = useMemo(() => {
    const maxPicksForWeek = getMaxPicksForWeek(week);
    return userLocksCount >= maxPicksForWeek;
  }, [userLocksCount, getMaxPicksForWeek, week]);

  // Check if future week locks are allowed for this week
  const [futureWeekLockAllowed, setFutureWeekLockAllowed] = useState(true);

  useEffect(() => {
    const checkFutureWeekLock = async () => {
      try {
        const allowed = await isFutureWeekLockAllowed(week);
        setFutureWeekLockAllowed(allowed);
      } catch (error) {
        console.error('Error checking future week lock allowance:', error);
        setFutureWeekLockAllowed(false);
      }
    };

    checkFutureWeekLock();
  }, [week, isFutureWeekLockAllowed]);

  const [viewMode, setViewMode] = useState('myPicks');
  const contentRef = useRef(null);
  const scrollbarRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [byeWeekRequiredSetting, setByeWeekRequiredSetting] = useState(true); // Default to true for safety

  const currentWeekData = calendar?.find(weekData => weekData.week === week);
  const weekNotes = currentWeekData ? currentWeekData.notes || '' : '';

  // Get week display label using the same logic as other components
  const getWeekDisplayLabel = () => {
    if (currentWeekData?.season_type === 'postseason' && currentWeekData?.week >= 17) {
      const bowlWeekNumber = currentWeekData.week - 16; // Week 17 = BW1, Week 18 = BW2
      return `BW${bowlWeekNumber}`;
    }
    return `Week ${week}`;
  };

  useEffect(() => {
    console.log('Fetching user season locks...');
    fetchUserSeasonLocks();
  }, [fetchUserSeasonLocks]);

  // Fetch bye week requirement setting
  useEffect(() => {
    const fetchByeWeekSetting = async () => {
      try {
        const response = await getByeWeekRequirement();
        setByeWeekRequiredSetting(response.data.bye_week_required);
      } catch (error) {
        console.error('Error fetching bye week requirement setting:', error);
        // Default to true if fetch fails (safer to enforce)
        setByeWeekRequiredSetting(true);
      }
    };
    fetchByeWeekSetting();
  }, []);

  const totalSeasonStats = useMemo(() => {
    if (!calendar || !user || !userSeasonLocks) {
      console.log('Missing required data for totalSeasonStats:', {
        hasCalendar: !!calendar,
        hasUser: !!user,
        seasonLocksCount: userSeasonLocks?.length,
        userSeasonLocks
      });
      return { total: 0, remaining: 0 };
    }

    const totalPicks = calendar
      .filter(w => !w.hide_week)
      .reduce((sum, week) => sum + week.max_picks, 0);

    const userLockedCount = userSeasonLocks.length;

    return {
      total: totalPicks,
      remaining: totalPicks - userLockedCount
    };
  }, [calendar, userSeasonLocks, user]);

  const pendingPicksWouldCompleteSeason = useMemo(() => {
    if (!totalSeasonStats.remaining) return false;

    const watchedCount = watchedPicks.filter(pick => pick.pickType !== 'bye').length;

    return watchedCount >= totalSeasonStats.remaining;
  }, [watchedPicks, totalSeasonStats]);

  const byeWeekRequired = useMemo(() => {
    // Only enforce if the setting is enabled
    if (!byeWeekRequiredSetting) return false;
    
    if (byeWeekState.bye_used) return false;

    const hasPendingBye = watchedPicks.some(pick =>
      pick.pickType === 'bye' && pick.status === 'pending'
    );

    return !byeWeekState.bye_used &&
           pendingPicksWouldCompleteSeason &&
           !hasPendingBye &&
           !byeWeekState.isPending;
  }, [
    byeWeekRequiredSetting,
    byeWeekState.bye_used,
    byeWeekState.isPending,
    pendingPicksWouldCompleteSeason,
    watchedPicks,
  ]);

  useEffect(() => {
    if (byeWeekRequired) {
      toggleByeWeekOption(true);
      alert(
        "You are selecting your final pick(s) of the season. " +
        "You must use your bye week before the season ends. " +
        "The bye week option has been enabled."
      );
    }
  }, [byeWeekRequired, toggleByeWeekOption]);

  const handleLockPick = useCallback((pick) => {
    requestDirectLock(pick, week);
  }, [requestDirectLock, week]);

  const handleLockBye = useCallback(() => {
    requestDirectLock({ pickType: 'bye', weekNumber: week }, week);
  }, [requestDirectLock, week]);

  const handleClearAllWatches = useCallback(() => {
    if (watchedPicks.length === 0) return;
    clearWatchesForWeek(week);
  }, [clearWatchesForWeek, week, watchedPicks.length]);

  const handleRemovePick = useCallback((pick) => {
    toggleWatchPick(
      pick.gameId,
      pick.pickType,
      week,
      0,  // watchLine
      0,  // liveLine
      isBetSlipOpen
    );
  }, [week, toggleWatchPick, isBetSlipOpen]);

  const renderPicks = (picksToRender, isUserPicks) => {
    let picks = [];

    if (isUserPicks && !byeWeekState.bye_used) {
      if (byeWeekRequired || byeWeekState.showOption) {
        const byePick = {
          id: 'bye-temp',
          pickType: 'bye',
          team: null,
          opponent: null,
          gameDate: null,
          watchLine: 0,
          liveLine: 0,
          status: byeWeekState.isPending ? 'pending' : 'watched'
        };

        picks.push(
          <PickItem
            key="bye-pick"
            pick={byePick}
            onTogglePending={() => toggleByeWeekPending(currentWeek)}
            onLock={handleLockBye}
            onRemove={() => !byeWeekRequired && toggleByeWeekOption(false)}
            isPending={byeWeekState.isPending}
            isDisabled={false}
            isUserPick={true}
            highlight={byeWeekRequired}
            className={byeWeekRequired ? styles.highlightedPick : ''}
            isFutureWeek={week > currentWeek}
            futureWeekLockAllowed={futureWeekLockAllowed}
          />
        );
      }
    }

    if (picksToRender?.length > 0) {
      picks = [
        ...picks,
        ...picksToRender.map((pick, index) => {
          if (!pick) return null;

          return (
            <PickItem
              key={pick.id || `temp-${index}`}
              pick={{
                ...pick,
                id: pick.id || `temp-${index}`,
                team: pick.team || 'Unknown Team',
                status: pick.status || 'watched',
              }}
              onRemove={() => handleRemovePick(pick)}
              isUserPick={isUserPicks}
              onLock={() => handleLockPick(pick)}
              showPadlockLock
              isDisabled={hasReachedMaxPicks}
              isFutureWeek={week > currentWeek}
              futureWeekLockAllowed={futureWeekLockAllowed}
            />
          );
        }).filter(Boolean)
      ];
    }

    return picks.length > 0 ? picks : <p className={styles.emptyState}>No picks to display.</p>;
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setIsLoading(false);
    };

    loadData();
  }, [week]);

  const renderLockedPicks = () => {
    const userLockedPicks = locks.filter(lock => lock.userId === user.id);

    if (userLockedPicks.length === 0) {
      return <p className={styles.emptyState}>No locked picks yet.</p>;
    }

    return userLockedPicks.map(lock => (
      <LockItem
        key={lock.id}
        lock={lock}
      />
    ));
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingIndicator}>
          <p>"Patience you must have, my young Padawan"</p>
        </div>
      );
    }

    return (
      <>
        {viewMode === 'myPicks' ? (
          <>
            <h3 className={styles.centeredHeader}>Locked Picks</h3>
            <div className={styles.lockList}>
              {renderLockedPicks()}
            </div>
            <h3 className={styles.centeredHeader}>Watch List</h3>
            <ul className={styles.pickList}>
              {renderPicks(watchedPicks, true)}
            </ul>
          </>
        ) : (
          <div className={styles.leaguePicks}>
            {locks.length > 0 ? (
              <ul className={styles.leagueLockList}>
                {locks
                  .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                  .map(lock => (
                    <LeagueLockItem
                      key={lock.id}
                      lock={lock}
                      currentUserId={user.id}
                      weekNumber={week}
                      hasUserReachedMaxLocks={hasReachedMaxPicks}
                      userLocksCount={hasReachedMaxPicks ? maxPicks : userLocksCount}
                      maxPicks={maxPicks}
                    />
                  ))}
              </ul>
            ) : (
              <p className={styles.emptyState}>No locks have been made yet.</p>
            )}
          </div>
        )}
        <div className={styles.bottomPadding}></div>
      </>
    );
  };

  useEffect(() => {
    const content = contentRef.current;
    const scrollbar = scrollbarRef.current;
    if (!content || !scrollbar) return;

    const updateScrollbar = () => {
      const { scrollTop, scrollHeight, clientHeight } = content;
      const scrollableHeight = scrollHeight - clientHeight;

      if (scrollableHeight <= 0) {
        scrollbar.style.display = 'none';
      } else {
        scrollbar.style.display = 'block';
        const thumbHeight = Math.max(20, (clientHeight / scrollHeight) * clientHeight);
        const thumbTop = (scrollTop / scrollableHeight) * (clientHeight - thumbHeight);

        scrollbar.style.height = `${thumbHeight}px`;
        scrollbar.style.top = `${thumbTop}px`;
      }
    };

    content.addEventListener('scroll', updateScrollbar);
    window.addEventListener('resize', updateScrollbar);

    updateScrollbar();

    return () => {
      content.removeEventListener('scroll', updateScrollbar);
      window.removeEventListener('resize', updateScrollbar);
    };
  }, []);


  const maxPicks = useMemo(() => getMaxPicksForWeek(week), [getMaxPicksForWeek, week]);

  const calculateAvailablePicks = useMemo(() => {
    if (!games || !locks) return 0;

    // Only consider scheduled games
    const scheduledGames = games.filter(game => game.status === 'scheduled');

    return scheduledGames.reduce((total, game) => {
      // Get all locks for this game
      const gameLocksCount = locks.filter(lock => lock.gameId === game.id).length;

      let availablePicksForGame = 0;

      // Add spread-based picks if available
      if (game.betting?.spread !== null) {
        availablePicksForGame += 2; // home and away
      }

      // Add over/under picks if available
      if (game.betting?.over_under !== null) {
        availablePicksForGame += 2; // over and under
      }

      // Subtract the number of locks already made for this game
      availablePicksForGame = Math.max(0, availablePicksForGame - gameLocksCount);

      return total + availablePicksForGame;
    }, 0);
  }, [games, locks]);

  const renderPickCounts = useMemo(() => {
    if (viewMode === 'leaguePicks') {
      const totalLocksForWeek = locks?.length || 0;
      const totalPossiblePicks = activeUserCount * maxPicks;
      return (
        <>
          <div>Picks Locked: {totalLocksForWeek} of {totalPossiblePicks}</div>
          {totalLocksForWeek < totalPossiblePicks && (
            <div className={styles.availablePicks}>
              Available Picks Remaining: {calculateAvailablePicks}
            </div>
          )}
        </>
      );
    }

    const watchedCount = watchedPicks.length;
    const lockedCount = locks.filter(lock => lock.userId === user.id).length;

    const parts = [];

    if (lockedCount === maxPicks) {
      return `Locked: ${lockedCount} of ${maxPicks}`;
    }

    parts.push(`Watches: ${watchedCount}`);

    if (lockedCount > 0) {
      parts.push(`Locked: ${lockedCount} of ${maxPicks}`);
    }

    return parts.join(', ');
  }, [viewMode, locks, watchedPicks, user.id, maxPicks, activeUserCount, calculateAvailablePicks]);

  useEffect(() => {
    if (viewMode === 'leaguePicks') {
      fetchActiveUserCount();
    }
  }, [viewMode, fetchActiveUserCount]);

  const isLastAvailableWeek = useMemo(() => {
    if (!calendar || !week) return false;
    const visibleWeeks = calendar.filter(w => !w.hide_week);
    return week === Math.max(...visibleWeeks.map(w => w.week));
  }, [calendar, week]);

  const getRemainingFinalWeekPicks = useCallback(() => {
    if (!isLastAvailableWeek) return null;
    const lockedPicksCount = locks.filter(lock => lock.userId === user.id).length;
    return maxPicks - lockedPicksCount;
  }, [isLastAvailableWeek, locks, user.id, maxPicks]);

  const renderByeWeekToggle = () => {
    if (byeWeekState.bye_used) return null;

    const userLocksForWeek = locks.filter(lock => lock.userId === user.id);
    const remainingFinalWeekPicks = getRemainingFinalWeekPicks();

    // Only force bye week if the setting is enabled AND it's the last pick
    const forceByeWeek = byeWeekRequiredSetting && remainingFinalWeekPicks === 1;

    if (userLocksForWeek.length >= maxPicks) return null;

    return (
      <div className={styles.byeToggleContainer}>
        <label className={`${styles.byeToggleLabel} ${forceByeWeek ? styles.disabled : ''}`}>
          <input
            type="checkbox"
            checked={forceByeWeek || byeWeekState.showOption}
            onChange={(e) => !forceByeWeek && toggleByeWeekOption(e.target.checked)}
            className={styles.byeToggleInput}
            disabled={forceByeWeek}
          />
          {' '}Show Bye Week Option
          {forceByeWeek && (
            <span className={styles.forcedByeMessage}>
              (Bye week must be used before season ends)
            </span>
          )}
        </label>
      </div>
    );
  };

  const renderWatchListActions = () => (
    <>
      {watchedPicks.length > 0 && (
        <button
          type="button"
          className={styles.clearWatchesButton}
          onClick={handleClearAllWatches}
        >
          Clear All Watches
        </button>
      )}
    </>
  );

  return (
    <div className={styles.pickSlip}>
      <button
        className={styles.pickSlipTab}
        onClick={toggleOpen}
        onKeyDown={(e) => e.key === 'Enter' && toggleOpen()}
        aria-label="Toggle pick slip"
      >
        <DragHandleIcon />
      </button>
      <div className={styles.pickSlipHeader}>
        <h2 className={styles.title}>{getWeekDisplayLabel()} Pick Slip {weekNotes && `(${weekNotes})`}</h2>
        <button className={styles.closeButton} onClick={onClose}>→</button>
      </div>
      <div className={styles.segmentedControl}>
        <button
          className={`${styles.segmentButton} ${viewMode === 'myPicks' ? styles.active : ''}`}
          onClick={() => setViewMode('myPicks')}
        >
          My Picks
        </button>
        <button
          className={`${styles.segmentButton} ${viewMode === 'leaguePicks' ? styles.active : ''}`}
          onClick={() => setViewMode('leaguePicks')}
        >
          League Picks
        </button>
      </div>
      <div className={styles.pickCounts}>
        <span className={styles.pickCountText}>
          {renderPickCounts}
        </span>
      </div>
      <div className={styles.pickSlipContentWrapper}>
        <div className={styles.pickSlipContent} ref={contentRef}>
          {renderContent()}
        </div>
        <div className={styles.scrollbarTrack}>
          <div className={styles.scrollbarThumb} ref={scrollbarRef}></div>
        </div>
      </div>
      {viewMode === 'myPicks' && (
        <div className={styles.buttonContainer}>
          {renderWatchListActions()}
          {!byeWeekRequired && renderByeWeekToggle()}
        </div>
      )}
      {verificationState.isOpen && (
        <ConfirmationDialog
          title="Line Mismatch"
          message="The Live LOTW Sportsbook line does not match the submitted lock due to stale data or data tampering. Please accept the new line(s) or cancel the lock."
          details={verificationState.mismatchedLines.map(line => line.displayMessage)}
          confirmLabel="Accept New Line(s)"
          cancelLabel="Cancel Lock"
          onConfirm={() => handleVerificationResponse(true)}
          onCancel={() => handleVerificationResponse(false)}
        />
      )}
    </div>
  );
};

BetSlip.propTypes = {
  week: PropTypes.number.isRequired,
  calendar: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  watchedPicks: PropTypes.array.isRequired,
  setWatchedPicks: PropTypes.func.isRequired,
  locks: PropTypes.array.isRequired,
  toggleOpen: PropTypes.func.isRequired,
  isBetSlipOpen: PropTypes.bool.isRequired,
  games: PropTypes.array.isRequired
};

export default BetSlip;
